import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/api";
import { useLang } from "@/i18n";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { Link2, Copy, Dumbbell, Apple } from "lucide-react";

export default function Clients() {
  const { t } = useLang();
  const [clients, setClients] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [nutriPlans, setNutriPlans] = useState([]);
  const [search, setSearch] = useState("");
  const [inviteProgram, setInviteProgram] = useState("");
  const [inviteLabel, setInviteLabel] = useState("");
  const [inviteUrl, setInviteUrl] = useState("");
  const [expiryEdits, setExpiryEdits] = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [newClient, setNewClient] = useState({ name: "", email: "", password: "", weight_kg: "", height_cm: "", allergies: "", injuries: "", package_expires_at: "", assigned_program_id: "" });
  const [createdClient, setCreatedClient] = useState(null);
  const [showCoachCreate, setShowCoachCreate] = useState(false);
  const [newCoach, setNewCoach] = useState({ name: "", email: "", password: "", is_admin: true });

  const createCoach = async () => {
    try {
      await api.post("/coach/coaches", { ...newCoach });
      toast.success(t("common.save") + " ✓");
      setNewCoach({ name: "", email: "", password: "", is_admin: true });
      setShowCoachCreate(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const nav = [
    { to: "/coach", key: "dashboard", label: t("nav.dashboard") },
    { to: "/coach/clients", key: "clients", label: t("nav.clients"), end: true },
    { to: "/coach/exercises", key: "exercises", label: t("nav.exercises") },
    { to: "/coach/programs", key: "programs", label: t("nav.programs") },
    { to: "/coach/nutrition", key: "nutrition", label: t("nav.nutrition") },
    { to: "/coach/supplements", key: "supplements", label: t("nav.supplements") },
    { to: "/coach/stretching", key: "stretching", label: t("nav.stretching") },
    { to: "/coach/shop", key: "shop", label: t("nav.shop") },
    { to: "/coach/testimonials", key: "testimonials", label: t("nav.testimonials") },
    { to: "/coach/education", key: "education", label: t("nav.education") },
  ];

  const load = () => {
    api.get("/coach/clients").then((r) => setClients(r.data));
    api.get("/coach/coaches").then((r) => setCoaches(r.data));
    api.get("/programs").then((r) => setPrograms(r.data));
    api.get("/nutrition-plans").then((r) => setNutriPlans(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return clients.filter((c) => c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q));
  }, [clients, search]);

  const progMap = useMemo(() => Object.fromEntries(programs.map((p) => [p._id, p])), [programs]);
  const nutriMap = useMemo(() => {
    const m = {};
    for (const p of nutriPlans) if (p.client_id && !m[p.client_id]) m[p.client_id] = p;
    return m;
  }, [nutriPlans]);

  const generateInvite = async () => {
    try {
      const { data } = await api.post("/coach/invites", { program_id: inviteProgram || null, label: inviteLabel });
      setInviteUrl(data.url);
      toast.success(t("common.copied").replace("!", "") + " ✓");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const createClient = async () => {
    try {
      const { data } = await api.post("/coach/clients", {
        name: newClient.name, email: newClient.email,
        password: newClient.password || null,
        weight_kg: newClient.weight_kg ? Number(newClient.weight_kg) : null,
        height_cm: newClient.height_cm ? Number(newClient.height_cm) : null,
        allergies: newClient.allergies, injuries: newClient.injuries,
        package_expires_at: newClient.package_expires_at || null,
        assigned_program_id: newClient.assigned_program_id || null,
      });
      setCreatedClient(data.temp_password ? { email: data.email, temp_password: data.temp_password } : null);
      toast.success(t("common.save") + " ✓");
      setNewClient({ name: "", email: "", password: "", weight_kg: "", height_cm: "", allergies: "", injuries: "", package_expires_at: "", assigned_program_id: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(inviteUrl);
    toast.success(t("common.copied"));
  };

  const patch = async (id, payload) => {
    try {
      await api.patch(`/coach/clients/${id}`, payload);
      toast.success(t("common.save") + " ✓");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <Layout nav={nav}>
      <div data-testid="clients-page">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h1 className="text-3xl sm:text-4xl font-black uppercase tracking-tight">{t("nav.clients")}</h1>
          <div className="flex gap-2">
            <button data-testid="new-coach-button" onClick={() => setShowCoachCreate(!showCoachCreate)} className="kc-btn-secondary !py-2.5">
              {t("coach.newCoach")}
            </button>
            <button data-testid="new-client-button" onClick={() => setShowCreate(!showCreate)} className="kc-btn-primary !py-2.5">
              {t("coach.newClient")}
            </button>
          </div>
        </div>

        {showCoachCreate && (
          <div className="kc-card p-5 mb-8 space-y-3" data-testid="create-coach-form">
            <p className="kc-eyebrow">{t("coach.newCoach")}</p>
            <div className="grid sm:grid-cols-3 gap-3">
              <input data-testid="coach-name-input" placeholder={t("register.name")} value={newCoach.name}
                onChange={(e) => setNewCoach({ ...newCoach, name: e.target.value })} className="kc-input" />
              <input data-testid="coach-email-input" type="email" placeholder={t("login.email")} value={newCoach.email}
                onChange={(e) => setNewCoach({ ...newCoach, email: e.target.value })} className="kc-input" />
              <input data-testid="coach-password-input" placeholder={t("login.password")} value={newCoach.password}
                onChange={(e) => setNewCoach({ ...newCoach, password: e.target.value })} className="kc-input" />
            </div>
            <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
              <input data-testid="coach-admin-checkbox" type="checkbox" checked={newCoach.is_admin}
                onChange={(e) => setNewCoach({ ...newCoach, is_admin: e.target.checked })} className="w-4 h-4 accent-primary" />
              {t("coach.adminCoach")}
            </label>
            <p className="text-xs text-muted-foreground">{t("coach.adminCoachHint")}</p>
            <button data-testid="create-coach-button" onClick={createCoach}
              disabled={!newCoach.name.trim() || !newCoach.email.trim() || newCoach.password.length < 8}
              className="kc-btn-primary w-full !py-2.5">
              {t("coach.createCoach")}
            </button>
          </div>
        )}

        {showCreate && (
          <div className="kc-card p-5 mb-8 space-y-3" data-testid="create-client-form">
            <p className="kc-eyebrow">{t("coach.newClient")}</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <input data-testid="manual-name-input" placeholder={t("register.name")} value={newClient.name}
                onChange={(e) => setNewClient({ ...newClient, name: e.target.value })} className="kc-input" />
              <input data-testid="manual-email-input" type="email" placeholder={t("login.email")} value={newClient.email}
                onChange={(e) => setNewClient({ ...newClient, email: e.target.value })} className="kc-input" />
              <input data-testid="manual-password-input" placeholder={t("coach.passwordOptional")} value={newClient.password}
                onChange={(e) => setNewClient({ ...newClient, password: e.target.value })} className="kc-input" />
              <input data-testid="manual-expiry-input" type="date" title={t("coach.expiry")} value={newClient.package_expires_at}
                onChange={(e) => setNewClient({ ...newClient, package_expires_at: e.target.value })} className="kc-input" />
              <input data-testid="manual-weight-input" type="number" min="0" step="0.1" placeholder={t("field.weight")} value={newClient.weight_kg}
                onChange={(e) => setNewClient({ ...newClient, weight_kg: e.target.value })} className="kc-input" />
              <input data-testid="manual-height-input" type="number" min="0" placeholder={t("field.height")} value={newClient.height_cm}
                onChange={(e) => setNewClient({ ...newClient, height_cm: e.target.value })} className="kc-input" />
            </div>
            <textarea data-testid="manual-allergies-input" rows={2} placeholder={t("field.allergies")} value={newClient.allergies}
              onChange={(e) => setNewClient({ ...newClient, allergies: e.target.value })} className="kc-input" />
            <textarea data-testid="manual-injuries-input" rows={2} placeholder={t("field.injuries")} value={newClient.injuries}
              onChange={(e) => setNewClient({ ...newClient, injuries: e.target.value })} className="kc-input" />
            <select data-testid="manual-program-select" value={newClient.assigned_program_id}
              onChange={(e) => setNewClient({ ...newClient, assigned_program_id: e.target.value })} className="kc-input">
              <option value="">{t("coach.selectProgram")}</option>
              {programs.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
            </select>
            <button data-testid="create-client-button" onClick={createClient}
              disabled={!newClient.name.trim() || !newClient.email.trim()} className="kc-btn-primary w-full !py-2.5">
              {t("coach.createClient")}
            </button>
            {createdClient && (
              <div className="bg-secondary/50 rounded-lg p-3" data-testid="temp-password-box">
                <p className="text-xs text-muted-foreground">{t("coach.generatedPassword")}</p>
                <p className="font-mono text-accent">{createdClient.email} — {createdClient.temp_password}</p>
              </div>
            )}
          </div>
        )}

        <div className="kc-card p-5 mb-8" data-testid="invite-generator">
          <p className="kc-eyebrow mb-3 flex items-center gap-2"><Link2 className="w-4 h-4" />{t("coach.newInvite")}</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <select data-testid="invite-program-select" value={inviteProgram} onChange={(e) => setInviteProgram(e.target.value)}
              className="kc-input sm:flex-1">
              <option value="">{t("coach.selectProgram")}</option>
              {programs.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
            </select>
            <input data-testid="invite-label-input" placeholder={t("coach.inviteLabel")} value={inviteLabel}
              onChange={(e) => setInviteLabel(e.target.value)} className="kc-input sm:flex-1" />
            <button data-testid="generate-invite-button" onClick={generateInvite} className="kc-btn-primary !py-2.5">
              {t("coach.generate")}
            </button>
          </div>
          {inviteUrl && (
            <div className="mt-3 flex items-center gap-2 bg-secondary/50 rounded-lg p-3" data-testid="invite-url-box">
              <code className="text-xs sm:text-sm text-accent flex-1 break-all font-mono">{inviteUrl}</code>
              <button data-testid="copy-invite-button" onClick={copyUrl} className="kc-btn-secondary !px-3 !py-1.5">
                <Copy className="w-4 h-4" />
              </button>
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-2">{t("coach.trialNote")}</p>
        </div>

        <input data-testid="client-search-input" placeholder={t("common.search")} value={search}
          onChange={(e) => setSearch(e.target.value)} className="kc-input mb-4 max-w-sm" />

        {filtered.length === 0 ? (
          <p className="text-muted-foreground" data-testid="clients-empty">{t("coach.noClients")}</p>
        ) : (
          <div className="space-y-3">
            {filtered.map((c) => (
              <div key={c._id} className="kc-card p-4 flex flex-col lg:flex-row lg:items-center gap-3" data-testid="coach-dashboard-client-row">
                <div className="flex-1 min-w-0">
                  <p className="font-bold truncate">{c.name}</p>
                  <p className="text-sm text-muted-foreground truncate">{c.email}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs">
                    {progMap[c.assigned_program_id] ? (
                      <Link to={`/coach/programs?view=${c.assigned_program_id}`} data-testid={`client-program-link-${c._id}`}
                        className="text-primary font-semibold hover:underline inline-flex items-center gap-1">
                        <Dumbbell className="w-3.5 h-3.5" /> {progMap[c.assigned_program_id].name}
                      </Link>
                    ) : (
                      <span className="text-muted-foreground inline-flex items-center gap-1" data-testid={`client-program-none-${c._id}`}>
                        <Dumbbell className="w-3.5 h-3.5" /> —
                      </span>
                    )}
                    {nutriMap[c._id] ? (
                      <Link to={`/coach/nutrition?edit=${nutriMap[c._id]._id}`} data-testid={`client-nutrition-link-${c._id}`}
                        className="text-primary font-semibold hover:underline inline-flex items-center gap-1">
                        <Apple className="w-3.5 h-3.5" /> {nutriMap[c._id].name}
                      </Link>
                    ) : (
                      <span className="text-muted-foreground inline-flex items-center gap-1" data-testid={`client-nutrition-none-${c._id}`}>
                        <Apple className="w-3.5 h-3.5" /> —
                      </span>
                    )}
                  </div>
                </div>
                {c.health_status && (
                  <span data-testid={`client-health-badge-${c._id}`} title={t(`hc.status.${c.health_status}`)}
                    className={`kc-badge inline-flex items-center gap-1.5 ${c.health_status === "red" ? "bg-destructive/15 text-destructive" : c.health_status === "yellow" ? "bg-amber-500/15 text-amber-500" : "bg-green-500/15 text-green-500"}`}>
                    <span className={`w-2 h-2 rounded-full ${c.health_status === "red" ? "bg-destructive" : c.health_status === "yellow" ? "bg-amber-500" : "bg-green-500"}`} />
                    {t("hc.badge")}
                  </span>
                )}
                <span data-testid="client-status-badge"
                  className={`kc-badge ${c.blocked ? "bg-destructive/15 text-destructive" : "bg-green-500/15 text-green-500"}`}>
                  {c.blocked ? t("coach.status.expired") : t("coach.status.active")}
                </span>
                <select data-testid={`assign-coach-select-${c._id}`} value={c.coach_id || ""}
                  onChange={(e) => patch(c._id, { coach_id: e.target.value || null })}
                  className="kc-input !w-auto !py-1.5 text-sm" title={t("coach.assignCoach")}>
                  <option value="">{t("coach.unassigned")}</option>
                  {coaches.map((co) => <option key={co._id} value={co._id}>{co.name}</option>)}
                </select>
                <select data-testid={`assign-program-select-${c._id}`} value={c.assigned_program_id || ""}
                  onChange={(e) => patch(c._id, { assigned_program_id: e.target.value || null })}
                  className="kc-input !w-auto !py-1.5 text-sm" title={t("coach.assignProgram")}>
                  <option value="">{t("coach.none")}</option>
                  {programs.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
                </select>
                <div className="flex items-center gap-2">
                  <input data-testid={`expiry-input-${c._id}`} type="date"
                    value={expiryEdits[c._id] ?? (c.package_expires_at ? c.package_expires_at.slice(0, 10) : "")}
                    onChange={(e) => setExpiryEdits({ ...expiryEdits, [c._id]: e.target.value })}
                    className="kc-input !w-auto !py-1.5 text-sm" title={t("coach.expiry")} />
                  <button data-testid={`expiry-save-${c._id}`} onClick={() => patch(c._id, { package_expires_at: expiryEdits[c._id] || null })}
                    className="kc-btn-secondary !px-3 !py-1.5 text-xs">{t("common.save")}</button>
                </div>
                <Link data-testid={`client-detail-link-${c._id}`} to={`/coach/clients/${c._id}`}
                  className="kc-btn-ghost text-sm">{t("coach.viewDetail")}</Link>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
